import {
  Chip,
  IconButton,
  Spinner,
  Typography
} from '@material-tailwind/react'
import {
  FiEdit2,
  FiEdit3,
  FiFileText,
  FiMoreHorizontal,
  FiRotateCcw
} from 'react-icons/fi'

import {
  Body,
  Cell,
  Header,
  HeaderCell,
  HeaderRow,
  Row,
  Table
} from '@table-library/react-table-library/table'

import { usePagination } from '@table-library/react-table-library/pagination'
import { useTheme } from '@table-library/react-table-library/theme'
import { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useSelector } from 'react-redux'
import {
  createSearchParams,
  generatePath,
  Link,
  useNavigation,
  useSearchParams
} from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { HasAccess } from '@permify/react-role'
import PropTypes from 'prop-types'
import { Overlay } from '@/shared/components/overlay'
import { Pagination } from '@/shared/components/pagination'
import { useGetEmployeesQuery } from '@/store/services'
import { isEmpty } from 'lodash'
import { LinkWithQuery } from '@/shared/components/link-with-query'
import { attachment } from '@/router/utils'

const EmployeeActionsMenu = ({
  employee,
  baseSearchParams,
  employeeContractIdForAttachments
}) => {
  const [open, setOpen] = useState(false)
  const menuRef = useRef(null)
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 })

  const setMenuPositionFromRect = (buttonRect) => {
    const MENU_WIDTH = 208
    const viewportRight = window.innerWidth - 8
    const preferredLeft = buttonRect.left
    const clampedLeft = Math.min(preferredLeft, viewportRight - MENU_WIDTH)

    setMenuPosition({
      top: buttonRect.bottom + 4,
      left: Math.max(8, clampedLeft)
    })
  }

  useEffect(() => {
    if (!open) return

    const handleClickOutside = (event) => {
      const isOutsideMenu = !menuRef.current?.contains(event.target)
      if (isOutsideMenu) {
        setOpen(false)
      }
    }

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setOpen(false)
      }
    }

    const handleViewportChange = () => {
      setOpen(false)
    }

    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleEscape)
    window.addEventListener('resize', handleViewportChange)
    window.addEventListener('scroll', handleViewportChange, true)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleEscape)
      window.removeEventListener('resize', handleViewportChange)
      window.removeEventListener('scroll', handleViewportChange, true)
    }
  }, [open])

  const closeMenu = () => setOpen(false)
  const toggleMenu = (event) => {
    if (open) {
      setOpen(false)
      return
    }

    setMenuPositionFromRect(event.currentTarget.getBoundingClientRect())
    setOpen(true)
  }

  return (
    <div className='relative inline-flex'>
      <div className='inline-flex'>
        <IconButton
          variant='text'
          className='focus:outline-none'
          onClick={toggleMenu}
        >
          <FiMoreHorizontal size='1rem' />
        </IconButton>
      </div>

      {open && createPortal(
        <div
          ref={menuRef}
          style={{ top: `${menuPosition.top}px`, left: `${menuPosition.left}px` }}
          className='fixed z-[9999] min-w-52 rounded-md border border-blue-gray-50 bg-white py-1 shadow-xl'
        >
          <HasAccess permissions='employee#attachments'>
            <Link
              onClick={closeMenu}
              className='flex items-center gap-2 px-3 py-2 hover:bg-blue-gray-50'
              to={`${attachment('employees', employee.id)}?${createSearchParams({
                ...baseSearchParams,
                ...(employeeContractIdForAttachments
                  ? {
                      employee_contract_id:
                        employeeContractIdForAttachments
                    }
                  : {})
              }).toString()}`}
            >
              <FiFileText size='1rem' />
              <Typography variant='small'>Documentos asociados</Typography>
            </Link>
          </HasAccess>

          <HasAccess permissions='employee#history'>
            <LinkWithQuery
              onClick={closeMenu}
              className='flex items-center gap-2 px-3 py-2 hover:bg-blue-gray-50'
              to={generatePath('/employees/:id/contract_history', {
                id: employee.id
              })}
            >
              <FiRotateCcw size='1rem' />
              <Typography variant='small'>Historial de contratos</Typography>
            </LinkWithQuery>
          </HasAccess>

          <HasAccess permissions='employee#edit'>
            <Link
              onClick={closeMenu}
              className='flex items-center gap-2 px-3 py-2 hover:bg-blue-gray-50'
              to={generatePath('/employees/edit/:id', {
                id: employee.id
              })}
            >
              <FiEdit2 />
              <Typography variant='small'>Editar</Typography>
            </Link>
          </HasAccess>

          <HasAccess
            permissions={[
              'contract_change#create',
              'contract_termination#create'
            ]}
          >
            <LinkWithQuery
              onClick={closeMenu}
              to={generatePath('/employees/:id/requests', {
                id: employee.id
              })}
              className='flex items-center gap-2 px-3 py-2 hover:bg-blue-gray-50'
            >
              <FiEdit3 />
              <Typography variant='small'>Modificaciones</Typography>
            </LinkWithQuery>
          </HasAccess>
        </div>,
        document.body
      )}
    </div>
  )
}

export const EmployeeTable = () => {
  // Preferir obtener los datos con los hooks RTK en lugar useAsyncValue(react-router). ver: https://reactrouter.com/en/main/guides/data-libs#invalidating-data-in-mutations
  const { data = { employees: [] } } = useGetEmployeesQuery(location.search, {
    skip: isEmpty(location.search)
  })
  const { state } = useNavigation()
  const INITIAL_PAGE = 1
  const PER_PAGE = 10
  const [searchParams, setSearchParams] = useSearchParams()
  const baseSearchParams = useMemo(
    () => Object.fromEntries(searchParams.entries()),
    [searchParams]
  )

  const { t } = useTranslation()
  const { user } = useSelector((state) => state)

  const formatDateDMY = (s) => {
    if (!s) return '';
    const [y, m, d] = s.split('-');
    return `${d}-${m}-${y}`;
  };

  const theme = useTheme({
    Table: `
        --data-table-library_grid-template-columns: 0.8fr 0.1fr 0.5fr 1fr 1fr repeat(4, 0.5fr)
      `,
    BaseCell: `
      &:nth-of-type(9) {
        right: 0px;
      }
    `
  })
  const controlAccesoTheme = useTheme({
    Table: `
        --data-table-library_grid-template-columns: repeat(3, 1fr);
      `
  })

  const isControlAcceso =
    (user?.current?.roles || [])[0].name === 'Control de acceso'

  const onPaginationChange = (_, state) => {
    const page = state.page + 1 || INITIAL_PAGE
    const perPage = searchParams.get('per_page') || PER_PAGE

    searchParams.set('page', page)
    searchParams.set('per_page', perPage)
    setSearchParams(searchParams)
  }

  const pagination = usePagination(
    data,
    {
      state: {
        page: searchParams.get('page') - 1 || 0,
        size: searchParams.get('per_page') || 10
      },
      onChange: onPaginationChange
    },
    {
      isServer: true
    }
  )

  return (
    <div className='relative'>
      {state === 'loading' && (
        <Overlay>
          <Spinner color='blue' />
        </Overlay>
      )}
      <Table
        theme={isControlAcceso ? controlAccesoTheme : theme}
        layout={{ custom: true }}
        data={{ nodes: data.employees }}
        pagination={pagination}
        className='h-max z-0'
      >
        {(tableList) => (
          <>
            <Header>
              <HeaderRow>
                <HeaderCell resize>{t('common.fullName')} - {t('documentNumber.one')}</HeaderCell>
                <HeaderCell hide={isControlAcceso} resize>
                  Contrato
                </HeaderCell>
                <HeaderCell hide={isControlAcceso} resize>
                  Rol persona
                </HeaderCell>
                <HeaderCell hide={isControlAcceso} resize>
                  Datos personales
                </HeaderCell>
                <HeaderCell hide={isControlAcceso} resize>
                  Cond. Contrato
                </HeaderCell>
                <HeaderCell resize>{t('status.one')}</HeaderCell>
                <HeaderCell resize>Acreditado</HeaderCell>
                <HeaderCell hide={isControlAcceso} resize>
                  NC
                </HeaderCell>
                <HeaderCell hide={isControlAcceso} pinRight resize>
                  Acciones
                </HeaderCell>
              </HeaderRow>
            </Header>

            <Body>
              {tableList.map(({ country, ...employee }) => {
                const employeeContractIdForAttachments =
                  employee?.employeeContractId || null

                return (
                  <Row key={employee.id} item={employee}>
                  <Cell>
                    {employee.fullName} <br />
                    {employee.documentNumber}
                  </Cell>
                  <Cell hide={isControlAcceso}>
                    {employee.activeContract?.contract ? (
                      <>
                        {`${employee.activeContract.contract.id} - ${employee.activeContract.contract.name}`}
                        <br />
                        {employee.activeContract.contract.company.name}
                      </>
                    ) : '-'}
                  </Cell>
                  <Cell hide={isControlAcceso}>
                    {employee.activeContract?.employeeRole?.name ||
                      t('employee.noAssignedRole')}
                  </Cell>
                  <Cell hide={isControlAcceso}>
                    <div className='flex flex-col justify-start items-start'>
                      <Typography variant='small' className='font-normal'>
                        {`${t('common.birthDate')}: ${
                            employee.birthDate
                              ? formatDateDMY(employee.birthDate)
                              : ''
                          }`}
                      </Typography>

                      <Typography variant='small' className='font-normal'>
                        {`Nacionalidad: ${country.name}`}
                      </Typography>

                      <Typography variant='small' className='font-normal'>
                        {`Género: ${t('gender.' + employee.gender)}`}
                      </Typography>

                      <Typography variant='small' className='font-normal'>
                        {`Discapacidad: ${t('common.' + employee.disability)}`}
                      </Typography>

                      <Typography variant='small' className='font-normal'>
                        {`Pensionado: ${t('common.' + employee.pensioner)}`}
                      </Typography>
                    </div>
                  </Cell>

                  <Cell hide={isControlAcceso}>
                    <div className='flex flex-col items-start justify-start h-full'>
                      <Typography variant='small' className='font-normal'>
                        {employee.activeContract.startDate
                          ? 'Inicio: ' + formatDateDMY(employee.activeContract.startDate)
                          : '-'}
                      </Typography>

                      <Typography variant='small' className='font-normal'>
                        {employee.activeContract.finishDate
                          ? 'Término: ' + formatDateDMY(employee.activeContract.finishDate)
                          : ''}
                      </Typography>

                      {employee.activeContract?.contractType && (
                        <Typography variant='small' className='font-normal'>
                          {'Tipo de Contrato: ' + t(employee.activeContract.contractType + '.one')}
                        </Typography>
                      )}
                    </div>
                  </Cell>

                  <Cell>
                    <Chip
                      variant='ghost'
                      color={employee.status === 'active' ? 'green' : 'red'}
                      size='sm'
                      className='capitalize text-center h-fit'
                      value={
                        employee.status === 'active'
                          ? t('common.active')
                          : t('common.inactive')
                      }
                    />
                  </Cell>

                  <Cell>
                    <div className='flex flex-col items-start gap-1'>
                      <Chip
                        variant='ghost'
                        color={employee.accredited === 'active' ? 'green' : 'red'}
                        size='sm'
                        className='capitalize text-center max-h-6'
                        value={employee.accredited === 'active' ? 'Si' : 'No'}
                      />
                      {employee.accredited === 'active' &&
                        employee.accreditationExpiration && (
                          <div className='flex flex-col leading-tight'>
                            <Typography variant='small' className='font-normal'>
                              Vence:
                            </Typography>
                            <Typography variant='small' className='font-normal'>
                              {formatDateDMY(employee.accreditationExpiration)}
                            </Typography>
                          </div>
                        )}
                    </div>
                  </Cell>
                  <Cell hide={isControlAcceso}>{employee.nonconformities}</Cell>
                  <Cell pinRight hide={isControlAcceso}>
                    <EmployeeActionsMenu
                      employee={employee}
                      baseSearchParams={baseSearchParams}
                      employeeContractIdForAttachments={employeeContractIdForAttachments}
                    />
                  </Cell>
                  </Row>
                )
              })}
            </Body>
          </>
        )}
      </Table>

      <div className='flex justify-center items-end'>
        <Pagination
          onPageChange={({ selected }) => pagination.fns.onSetPage(selected)}
          page={pagination.state.page}
          totalPage={data?.meta?.pagination?.total_pages}
        />
      </div>
    </div>
  )
}

EmployeeTable.propTypes = {
  data: PropTypes.shape({
    employees: PropTypes.array,
    meta: PropTypes.object
  })
}
